import React from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function HikeMap({ entries }) {
  // Filter entries that have valid GPS coordinates
  const entriesWithCoords = entries.filter(entry => 
    (entry.locationFrom?.lat && entry.locationFrom?.lng) ||
    (entry.locationTo?.lat && entry.locationTo?.lng)
  );

  if (entriesWithCoords.length === 0) {
    return (
      <div className="bg-gray-100 rounded-lg p-8 text-center text-gray-500">
        <p>📍 No GPS coordinates recorded yet.</p>
        <p className="text-sm mt-2">Location data will appear here once you start logging with GPS.</p>
      </div>
    );
  }

  // Build array of all coordinates for the path
  const pathCoordinates = [];
  const markers = [];

  // Sort entries by date
  const sortedEntries = [...entriesWithCoords].sort((a, b) => new Date(a.date) - new Date(b.date));

  sortedEntries.forEach((entry, index) => {
    const dateStr = new Date(entry.date).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short'
    });

    // Add "from" location
    if (entry.locationFrom?.lat && entry.locationFrom?.lng) {
      const fromCoord = [entry.locationFrom.lat, entry.locationFrom.lng];
      pathCoordinates.push(fromCoord);
      
      // Only add marker for first entry's "from" or if it has a name
      if (index === 0 || entry.locationFrom.name) {
        markers.push({
          position: fromCoord,
          name: entry.locationFrom.name || 'Start',
          date: dateStr,
          type: 'from'
        });
      }
    }

    // Add "to" location
    if (entry.locationTo?.lat && entry.locationTo?.lng) {
      const toCoord = [entry.locationTo.lat, entry.locationTo.lng];
      pathCoordinates.push(toCoord);
      
      markers.push({
        position: toCoord,
        name: entry.locationTo.name || `Day ${index + 1}`,
        date: dateStr,
        type: 'to',
        km: entry.kmTravelled
      });
    }
  });

  // Calculate map center and bounds
  const center = pathCoordinates.length > 0 
    ? [
        pathCoordinates.reduce((sum, c) => sum + c[0], 0) / pathCoordinates.length,
        pathCoordinates.reduce((sum, c) => sum + c[1], 0) / pathCoordinates.length
      ]
    : [-41.2865, 174.7762]; // Default to NZ

  return (
    <div className="bg-white shadow-md rounded-lg p-6 mb-8">
      <h3 className="text-xl font-semibold mb-4 text-gray-900">🗺️ Trail Map</h3>
      <div style={{ height: '400px', width: '100%' }} className="rounded-lg overflow-hidden">
        <MapContainer
          center={center}
          zoom={10}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {/* Draw the path */}
          {pathCoordinates.length > 1 && (
            <Polyline
              positions={pathCoordinates}
              color="#059669"
              weight={3}
              opacity={0.8}
            />
          )}
          
          {/* Add markers */}
          {markers.map((marker, index) => (
            <Marker key={index} position={marker.position}>
              <Popup>
                <div className="text-sm">
                  <strong>{marker.name}</strong>
                  <br />
                  {marker.date}
                  {marker.km && <><br />{marker.km} km</>}
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
      <p className="text-xs text-gray-500 mt-2">
        Showing {pathCoordinates.length} GPS points from {entriesWithCoords.length} entries
      </p>
    </div>
  );
}

export default HikeMap;